// Google Analytics 4 (GA4) Implementation for Golden Books
// Documentation: https://developers.google.com/analytics/devguides/collection/ga4

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// GA4 Configuration
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
const isProduction = process.env.NODE_ENV === 'production';
const isAnalyticsEnabled = process.env.REACT_APP_ENABLE_ANALYTICS === 'true';

// Initialize dataLayer and gtag function early so they're available before initGA completes
window.dataLayer = window.dataLayer || [];
window.gtag = function() {
  window.dataLayer.push(arguments);
};

// Initialize Google Analytics
export const initGA = (): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) {
    console.log('Google Analytics is disabled or not configured');
    return;
  }

  // Add gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We'll send page views manually
    debug_mode: !isProduction,
  });

  console.log('Google Analytics initialized');
};

// Page view tracking
export const logPageView = (path?: string, title?: string): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'page_view', {
    page_path: path || window.location.pathname + window.location.search,
    page_title: title || document.title,
    page_location: window.location.href,
  });
};

// E-commerce Events

// View item list (category page, search results)
export const logViewItemList = (items: any[], listName: string, listId?: string): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'view_item_list', {
    item_list_id: listId,
    item_list_name: listName,
    items: items.map((item, index) => ({
      item_id: item.id?.toString(),
      item_name: item.name || item.title,
      item_category: item.category?.name,
      price: item.price,
      currency: 'BGN',
      index: index,
    })),
  });
};

// View item (product detail page)
export const logViewItem = (product: any): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'view_item', {
    currency: 'BGN',
    value: product.discountPrice || product.price,
    items: [{
      item_id: product.id?.toString(),
      item_name: product.name || product.title,
      item_category: product.category?.name,
      item_brand: product.publisher,
      price: product.price,
      discount: product.discountPrice ? product.price - product.discountPrice : 0,
      quantity: 1,
    }],
  });
};

// Add to cart
export const logAddToCart = (product: any, quantity: number = 1): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  const price = product.discountPrice || product.price;
  
  window.gtag('event', 'add_to_cart', {
    currency: 'BGN',
    value: price * quantity,
    items: [{
      item_id: product.id?.toString(),
      item_name: product.name || product.title,
      item_category: product.category?.name,
      item_brand: product.publisher,
      price: price,
      quantity: quantity,
    }],
  });
};

// Remove from cart
export const logRemoveFromCart = (product: any, quantity: number = 1): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  const price = product.discountPrice || product.price;
  
  window.gtag('event', 'remove_from_cart', {
    currency: 'BGN',
    value: price * quantity,
    items: [{
      item_id: product.id?.toString(),
      item_name: product.name || product.title,
      item_category: product.category?.name,
      item_brand: product.publisher,
      price: price,
      quantity: quantity,
    }],
  });
};

// View cart
export const logViewCart = (cartItems: any[], totalValue: number): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'view_cart', {
    currency: 'BGN',
    value: totalValue,
    items: cartItems.map(item => ({
      item_id: item.product?.id?.toString(),
      item_name: item.product?.name || item.product?.title,
      item_category: item.product?.category?.name,
      item_brand: item.product?.publisher,
      price: item.product?.discountPrice || item.product?.price,
      quantity: item.quantity,
    })),
  });
};

// Begin checkout
export const logBeginCheckout = (cartItems: any[], totalValue: number): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'begin_checkout', {
    currency: 'BGN',
    value: totalValue,
    items: cartItems.map(item => ({
      item_id: item.product?.id?.toString(),
      item_name: item.product?.name || item.product?.title,
      item_category: item.product?.category?.name,
      item_brand: item.product?.publisher,
      price: item.product?.discountPrice || item.product?.price,
      quantity: item.quantity,
    })),
  });
};

// Purchase
export const logPurchase = (order: any, items: any[]): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'purchase', {
    transaction_id: order.orderNumber || order.order_number || order.id?.toString(),
    value: order.totalAmount ?? order.total_amount,
    currency: 'BGN',
    shipping: order.shippingCost ?? order.shipping_cost ?? 0,
    items: items.map(item => ({
      item_id: item.product?.id?.toString(),
      item_name: item.product?.name || item.product?.title,
      item_category: item.product?.category?.name,
      item_brand: item.product?.publisher,
      price: item.price,
      quantity: item.quantity,
    })),
  });
};

// Search
export const logSearch = (searchTerm: string, resultsCount?: number): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'search', {
    search_term: searchTerm,
    number_of_results: resultsCount,
  });
};

// User Events

// Sign up
export const logSignUp = (method: string = 'email'): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'sign_up', {
    method: method,
  });
};

// Login
export const logLogin = (method: string = 'email'): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'login', {
    method: method,
  });
};

// Custom Events

// Track language change
export const logLanguageChange = (language: string): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'language_change', {
    language: language,
  });
};

// Track currency view
export const logCurrencyView = (currency: string): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'currency_view', {
    currency: currency,
  });
};

// Track errors
export const logError = (error: string, fatal: boolean = false): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'exception', {
    description: error,
    fatal: fatal,
  });
};

// Enhanced E-commerce: Promotion tracking
export const logViewPromotion = (promotionName: string, promotionId?: string): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'view_promotion', {
    promotion_id: promotionId,
    promotion_name: promotionName,
    creative_name: promotionName,
    creative_slot: 'homepage_banner',
  });
};

// Track social shares
export const logShare = (method: string, contentType: string, itemId?: string): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'share', {
    method: method, // e.g., 'facebook', 'twitter', 'email'
    content_type: contentType, // e.g., 'product', 'article'
    item_id: itemId,
  });
};

// Timing events (for performance tracking)
export const logTiming = (name: string, value: number, category: string = 'Performance'): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('event', 'timing_complete', {
    name: name,
    value: value,
    event_category: category,
  });
};

// Set user properties
export const setUserProperties = (properties: Record<string, any>): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('set', 'user_properties', properties);
};

// Set user ID (for logged-in users)
export const setUserId = (userId: string): void => {
  if (!isAnalyticsEnabled || !GA_MEASUREMENT_ID) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId,
  });
};

// Export all functions as analytics object
const analytics = {
  init: initGA,
  pageView: logPageView,
  viewItemList: logViewItemList,
  viewItem: logViewItem,
  addToCart: logAddToCart,
  removeFromCart: logRemoveFromCart,
  viewCart: logViewCart,
  beginCheckout: logBeginCheckout,
  purchase: logPurchase,
  search: logSearch,
  signUp: logSignUp,
  login: logLogin,
  languageChange: logLanguageChange,
  currencyView: logCurrencyView,
  error: logError,
  viewPromotion: logViewPromotion,
  share: logShare,
  timing: logTiming,
  setUserProperties,
  setUserId,
};

export default analytics;